

<template>
  <div class="rd">

    <!-- ── HERO ── -->
    <div class="rd-hero">
      <div class="rd-hero__top">
        <div class="rd-hero__eyebrow">Reseller Hub</div>
        <v-btn icon small class="rd-hero__bell" @click="() => {}">
          <v-icon small color="rgba(255,255,255,.6)">mdi-bell-outline</v-icon>
        </v-btn>
      </div>

      <div class="rd-hero__balance-label">Solde disponible</div>
      <div class="rd-hero__balance">
        {{ formatLarge(dashboard.reseller_balance) }}
        <span class="rd-hero__currency">FCFA</span>
      </div>

      <div class="rd-hero__actions">
        <button
          class="rd-action-btn"
          :class="{ 'rd-action-btn--off': parseFloat(dashboard.reseller_balance) <= 0 }"
          @click="parseFloat(dashboard.reseller_balance) > 0 && openTransferDialog()"
        >
          <span class="rd-action-btn__icon"><v-icon small color="white">mdi-bank-transfer</v-icon></span>
          <span>Transférer</span>
        </button>
        <button class="rd-action-btn" @click="$router.push({ name: 'business_reseller.products' })">
          <span class="rd-action-btn__icon"><v-icon small color="white">mdi-plus</v-icon></span>
          <span>Catalogue</span>
        </button>
        <button class="rd-action-btn" @click="mobileTab = 'history'">
          <span class="rd-action-btn__icon"><v-icon small color="white">mdi-history</v-icon></span>
          <span>Historique</span>
        </button>
      </div>
    </div>

    <!-- ── STATS ── -->
    <div class="rd-stats">
      <div class="rd-stat">
        <div class="rd-stat__val rd-stat__val--green">{{ formatCurrency(dashboard.total_earned) }}</div>
        <div class="rd-stat__lbl">Total gagné</div>
      </div>
      <div class="rd-stat-sep"></div>
      <div class="rd-stat">
        <div class="rd-stat__val">{{ dashboard.product_count || 0 }}</div>
        <div class="rd-stat__lbl">Produits</div>
      </div>
      <div class="rd-stat-sep"></div>
      <div class="rd-stat">
        <div class="rd-stat__val">{{ transactions.length }}</div>
        <div class="rd-stat__lbl">Transactions</div>
      </div>
    </div>

    <!-- ── BODY ── -->
    <div class="rd-body">

      <!-- mobile tab bar -->
      <div class="rd-tabs hidden-md-and-up">
        <button class="rd-tab" :class="{ 'rd-tab--on': mobileTab === 'products'  }" @click="mobileTab = 'products'">Produits <span v-if="products.length" class="rd-tab__n">{{ products.length }}</span></button>
        <button class="rd-tab" :class="{ 'rd-tab--on': mobileTab === 'history'   }" @click="mobileTab = 'history'">Historique <span v-if="transactions.length" class="rd-tab__n">{{ transactions.length }}</span></button>
      </div>

      <!-- ── PRODUCTS ── -->
      <div class="rd-card" :class="{ 'rd-card--hidden': mobileTab === 'history' }">
        <div class="rd-card__head">
          <div class="rd-card__title">Mes produits</div>
          <div class="rd-search">
            <v-icon x-small color="#C4C9D4">mdi-magnify</v-icon>
            <input v-model="productSearch" class="rd-search__inp" placeholder="Rechercher…" type="search" />
          </div>
        </div>

        <!-- loading -->
        <div v-if="productsLoading" class="rd-skels">
          <div v-for="n in 4" :key="n" class="rd-skel-row">
            <div class="rd-skel rd-skel--sq"></div>
            <div style="flex:1">
              <div class="rd-skel rd-skel--line" style="width:52%"></div>
              <div class="rd-skel rd-skel--line mt2" style="width:33%"></div>
            </div>
          </div>
        </div>

        <!-- error -->
        <div v-else-if="productsError" class="rd-notice rd-notice--err">
          {{ productsError }}
          <button class="rd-retry" @click="loadDashboard">Réessayer</button>
        </div>

        <!-- empty -->
        <div v-else-if="!filteredProducts.length" class="rd-empty">
          <div class="rd-empty__title">Aucun produit</div>
          <div class="rd-empty__body">Ajoutez des produits pour commencer à gagner.</div>
          <button class="rd-cta" @click="$router.push({ name: 'business_reseller.products' })">Explorer le catalogue</button>
        </div>

        <!-- list -->
        <div v-else class="rd-product-list">
          <div v-for="item in filteredProducts" :key="item.id" class="rd-product">
            <div class="rd-product__img">
              <v-img :src="item.image || '/placeholder-product.png'" width="52" height="52" contain>
                <template v-slot:placeholder>
                  <div class="rd-product__img-ph"><v-icon color="#DDE1EA" x-small>mdi-image-off</v-icon></div>
                </template>
              </v-img>
            </div>
            <div class="rd-product__info">
              <div class="rd-product__name">{{ item.name }}</div>
              <div class="rd-product__meta">{{ item.seller_business }}</div>
              <div class="rd-product__price">{{ formatCurrency(item.resale_price) }}</div>
            </div>
            <div class="rd-product__share">
              <button class="rd-sh rd-sh--wa" @click="shareWhatsApp(item)"><v-icon x-small color="#25D366">mdi-whatsapp</v-icon></button>
              <button class="rd-sh rd-sh--fb" @click="shareFacebook(item)"><v-icon x-small color="#1877F2">mdi-facebook</v-icon></button>
              <button class="rd-sh rd-sh--sh" @click="shareNative(item)"><v-icon x-small color="#ffffff">mdi-share-variant</v-icon></button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── HISTORY ── -->
      <div class="rd-card" :class="{ 'rd-card--hidden': mobileTab === 'products' }">
        <div class="rd-card__head">
          <div class="rd-card__title">Historique</div>
          <div class="rd-ftabs">
            <button v-for="t in historyTabs" :key="t.value" class="rd-ftab" :class="{ 'rd-ftab--on': activeHistoryTab === t.value }" @click="activeHistoryTab = t.value">
              {{ t.label }}
              <span v-if="tabCount(t.value)" class="rd-ftab__n">{{ tabCount(t.value) }}</span>
            </button>
          </div>
        </div>

        <!-- loading -->
        <div v-if="transactionsLoading" class="rd-skels">
          <div v-for="n in 3" :key="n" class="rd-skel-row">
            <div class="rd-skel rd-skel--circle"></div>
            <div style="flex:1">
              <div class="rd-skel rd-skel--line" style="width:45%"></div>
              <div class="rd-skel rd-skel--line mt2" style="width:28%"></div>
            </div>
            <div class="rd-skel rd-skel--amount"></div>
          </div>
        </div>

        <!-- empty -->
        <div v-else-if="!filteredTransactions.length" class="rd-empty">
          <div class="rd-empty__title">Aucune transaction</div>
          <div class="rd-empty__body">{{ activeHistoryTab === 'ALL' ? 'Vos transactions apparaîtront ici.' : 'Rien dans cette catégorie.' }}</div>
        </div>

        <!-- feed -->
        <div v-else class="rd-feed">
          <div v-for="(txn, i) in filteredTransactions" :key="txn.reference || i" class="rd-txn">
            <div class="rd-txn__ico" :style="{ background: txnBg(txn.type || txn.transaction_type) }">
              <v-icon small :color="txnColor(txn.type || txn.transaction_type)">{{ txnIcon(txn.type || txn.transaction_type) }}</v-icon>
            </div>
            <div class="rd-txn__body">
              <div class="rd-txn__label">{{ txnLabel(txn.type || txn.transaction_type) }}</div>
              <div class="rd-txn__sub">
                <span v-if="txn.product">{{ txn.product.name || 'Produit #' + txn.product }}</span>
                <span v-else-if="txn.description">{{ txn.description }}</span>
                <span v-else>{{ txn.reference }}</span>
              </div>
            </div>
            <div class="rd-txn__right">
              <div class="rd-txn__amount" :class="isCredit(txn.type || txn.transaction_type) ? 'amt--pos' : 'amt--neg'">
                {{ isCredit(txn.type || txn.transaction_type) ? '+' : '−' }}{{ formatCurrency(txn.amount) }}
              </div>
              <div class="rd-txn__date">{{ txn.get_date_str || formatDate(txn.creation_date) }}</div>
            </div>
          </div>
        </div>
      </div>

    </div><!-- /body -->

    <!-- ── TRANSFER DIALOG ── -->
    <v-dialog v-model="transferDialog" :fullscreen="$vuetify.breakpoint.smAndDown" max-width="420" persistent>
      <div class="rd-modal" :class="{ 'rd-modal--full': $vuetify.breakpoint.smAndDown }">

        <!-- step bar -->
        <div class="rd-steps">
          <div class="rd-step" :class="{ 'rd-step--on': transferStep >= 1 }"></div>
          <div class="rd-step" :class="{ 'rd-step--on': transferStep >= 2 }"></div>
          <div class="rd-step" :class="{ 'rd-step--on': transferStep >= 3 }"></div>
        </div>

        <div class="rd-modal__head">
          <button v-if="transferStep > 1 && !transferring" class="rd-modal__nav" @click="transferStep--"><v-icon small>mdi-arrow-left</v-icon></button>
          <span v-else style="width:32px"></span>
          <span class="rd-modal__title">{{ ['Destination','Montant','Confirmer'][transferStep - 1] }}</span>
          <button class="rd-modal__nav" @click="closeTransferDialog"><v-icon small>mdi-close</v-icon></button>
        </div>

        <div class="rd-modal__avail">
          Disponible : <strong>{{ formatCurrency(dashboard.reseller_balance) }}</strong>
        </div>

        <div class="rd-modal__body">
          <v-form ref="transferForm" v-model="transferFormValid" lazy-validation>

            <!-- step 1 -->
            <div v-if="transferStep === 1">
              <label v-for="opt in destinationOptions" :key="opt.value" class="rd-dest" :class="{ 'rd-dest--on': transfer.destination === opt.value }">
                <input type="radio" :value="opt.value" v-model="transfer.destination" style="display:none" />
                <v-icon small :color="transfer.destination === opt.value ? '#5A55F4' : '#C4C9D4'">{{ opt.icon }}</v-icon>
                <div class="rd-dest__text">
                  <div class="rd-dest__name">{{ opt.text }}</div>
                  <div class="rd-dest__hint">{{ opt.hint }}</div>
                </div>
                <div class="rd-dest__dot" :class="{ 'rd-dest__dot--on': transfer.destination === opt.value }"></div>
              </label>

              <div v-if="transfer.destination === 'CAMPAIGN'" class="mt16">
                <v-select v-model="transfer.campaign_id" :items="campaigns" item-text="name" item-value="id" outlined dense rounded label="Campagne" prepend-inner-icon="mdi-flag" no-data-text="Aucune campagne" :rules="[v => !!v || 'Requis']"></v-select>
              </div>

              <button class="rd-modal__cta" type="button" @click="goStep2">Suivant</button>
            </div>

            <!-- step 2 -->
            <div v-if="transferStep === 2">
              <div class="rd-amt-display" :class="{ 'rd-amt-display--set': transfer.amount > 0 }">
                {{ transfer.amount > 0 ? formatCurrency(transfer.amount) : '— FCFA' }}
              </div>
              <v-text-field v-model.number="transfer.amount" type="number" outlined dense rounded prefix="FCFA" label="Montant" :rules="[ v => !!v || 'Requis', v => v > 0 || '> 0', v => v <= parseFloat(dashboard.reseller_balance) || 'Solde insuffisant' ]" class="mt16"></v-text-field>
              <div class="rd-pcts">
                <button v-for="p in [25,50,75,100]" :key="p" type="button" class="rd-pct" :class="{ 'rd-pct--on': isActivePct(p) }" @click="setQuickAmount(p)">{{ p }}%</button>
              </div>
              <button class="rd-modal__cta" type="button" @click="goStep3">Suivant</button>
            </div>

            <!-- step 3 -->
            <div v-if="transferStep === 3">
              <div class="rd-summary">
                <div class="rd-summary__row">
                  <span>Destination</span>
                  <strong>{{ transfer.destination === 'WALLET' ? 'Portefeuille' : 'Campagne' }}</strong>
                </div>
                <div class="rd-summary__row">
                  <span>Montant</span>
                  <strong class="rd-summary__big">{{ formatCurrency(transfer.amount) }}</strong>
                </div>
              </div>
              <div v-if="transferError" class="rd-err-msg">{{ transferError }}</div>
              <v-btn block rounded large color="#5A55F4" class="rd-modal__cta white--text mt16" :loading="transferring" :disabled="transferring" @click="doTransfer">
                Confirmer
              </v-btn>
            </div>

          </v-form>
        </div>
      </div>
    </v-dialog>

    <v-snackbar v-model="showSuccessSnackbar" color="#06C270" top right timeout="4000" rounded="lg">
      <v-icon small class="mr-2">mdi-check-circle</v-icon>{{ successMessage }}
    </v-snackbar>
    <v-snackbar v-model="copiedSnackbar" color="#1C1C28" bottom timeout="2000" rounded="lg">
      <v-icon small class="mr-2">mdi-link-variant</v-icon>Lien copié
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
export default {
  name: 'ResellerDashboard',
  data: () => ({
    dashboard: { reseller_balance:'0.00', total_earned:'0.00', product_count:0, products:[], transactions:[] },
    campaigns:[], products:[], transactions:[],
    transactionsLoading: false,
    activeHistoryTab: 'ALL',
    historyTabs: [
      { label:'Tout',       value:'ALL' },
      { label:'Ventes',     value:'RESALE' },
      { label:'Transferts', value:'TRANSFER' },
    ],
    productSearch:'',
    productsLoading: false,
    productsError: null,
    transferDialog: false,
    transferring: false,
    transferError: null,
    transferFormValid: false,
    transferStep: 1,
    showSuccessSnackbar: false,
    successMessage:'',
    transfer: { destination:'WALLET', campaign_id:null, amount:'' },
    copiedId:null, copiedSnackbar:false, copiedTimer:null,
    mobileTab: 'products',
    destinationOptions: [
      { text:'Portefeuille',       value:'WALLET',   icon:'mdi-wallet-outline', hint:'Transfert instantané' },
      { text:"Campagne d'épargne", value:'CAMPAIGN', icon:'mdi-flag-outline',   hint:'Vers votre objectif' },
    ],
  }),
  computed: {
    filteredProducts () {
      const q = (this.productSearch||'').toLowerCase().trim()
      if (!q) return this.products
      return this.products.filter(p => String(p.name||'').toLowerCase().includes(q) || String(p.seller_business||'').toLowerCase().includes(q))
    },
    filteredTransactions () {
      if (this.activeHistoryTab==='ALL') return this.transactions
      if (this.activeHistoryTab==='TRANSFER') return this.transactions.filter(t=>['TRANSFER_WALLET','TRANSFER_CAMPAIGN'].includes(t.type||t.transaction_type))
      return this.transactions.filter(t=>(t.type||t.transaction_type)===this.activeHistoryTab)
    },
  },
  mounted () { document.title='Reseller Dashboard'; this.loadAll() },
  methods: {
    async loadAll () { await Promise.all([this.loadDashboard(), this.loadCampaigns()]) },
    async loadDashboard () {
      this.productsLoading=true; this.productsError=null
      try {
        const res = await axios.get('/api/v1/market_place/reseller/dashboard/')
        this.dashboard=res.data; this.products=res.data.products||[]; this.transactions=res.data.transactions||[]
      } catch(err) {
        if(err.response?.status===403){this.$router.push('/business-account/market_place/reseller/apply');return}
        this.productsError=err.response?.data?.error||'Impossible de charger.'
      } finally { this.productsLoading=false }
    },
    async loadCampaigns () {
      try { this.campaigns=(await axios.get('/api/v1/market_place/seller/campaigns/')).data||[] } catch { this.campaigns=[] }
    },
    tabCount(v){
      if(v==='ALL') return this.transactions.length
      if(v==='TRANSFER') return this.transactions.filter(t=>['TRANSFER_WALLET','TRANSFER_CAMPAIGN'].includes(t.type||t.transaction_type)).length
      return this.transactions.filter(t=>(t.type||t.transaction_type)===v).length
    },
    goStep2(){
      if(this.transfer.destination==='CAMPAIGN'&&!this.transfer.campaign_id){this.$refs.transferForm.validate();return}
      this.transferStep=2
    },
    goStep3(){ if(!this.$refs.transferForm.validate()) return; if(!this.transfer.amount||this.transfer.amount<=0) return; this.transferStep=3 },
    setQuickAmount(p){ const b=parseFloat(this.dashboard.reseller_balance)||0; this.transfer.amount=Math.floor(b*p/100) },
    isActivePct(p){ const b=parseFloat(this.dashboard.reseller_balance)||0; return b>0&&this.transfer.amount===Math.floor(b*p/100) },
    openTransferDialog(){ this.resetTransfer(); this.transferDialog=true },
    closeTransferDialog(){ this.transferDialog=false; this.resetTransfer() },
    resetTransfer(){
      this.transfer={destination:'WALLET',campaign_id:null,amount:''}
      this.transferError=null; this.transferStep=1; this.transferring=false
      if(this.$refs.transferForm) this.$refs.transferForm.resetValidation()
    },
    async doTransfer(){
      this.transferError=null; this.transferring=true
      try {
        const p={destination:this.transfer.destination,amount:this.transfer.amount}
        if(this.transfer.destination==='CAMPAIGN') p.campaign_id=this.transfer.campaign_id
        const res=await axios.post('/api/v1/market_place/reseller/transfer/',p)
        this.successMessage=res.data.message||'Transfert effectué !'
        this.showSuccessSnackbar=true; this.transferDialog=false; this.resetTransfer()
        await this.loadDashboard()
      } catch(err){ this.transferError=err.response?.data?.error||'Échec. Réessayez.' }
      finally { this.transferring=false }
    },
    isCredit(t){ return t==='RESALE' },
    txnColor(t){ return{RESALE:'#06C270',TRANSFER_WALLET:'#5A55F4',TRANSFER_CAMPAIGN:'#F59E0B',REFUND:'#9BA3AF'}[t]||'#9BA3AF' },
    txnBg(t){ return{RESALE:'rgba(6,194,112,.08)',TRANSFER_WALLET:'rgba(90,85,244,.08)',TRANSFER_CAMPAIGN:'rgba(245,158,11,.08)',REFUND:'rgba(155,163,175,.08)'}[t]||'rgba(155,163,175,.08)' },
    txnIcon(t){ return{RESALE:'mdi-cash-plus',TRANSFER_WALLET:'mdi-wallet-outline',TRANSFER_CAMPAIGN:'mdi-flag-outline',REFUND:'mdi-cash-refund'}[t]||'mdi-help-circle' },
    txnLabel(t){ return{RESALE:'Vente',TRANSFER_WALLET:'Vers portefeuille',TRANSFER_CAMPAIGN:'Vers campagne',REFUND:'Remboursement'}[t]||t },
    shareLink(i){ return `https://myapp.pettycashglobal.com/market_place/items/${i.product_id||i.id}?r=${i.id}` },
    facebookShareLink(i){ return i.share_url||`https://api.pettycashglobal.com/share/product/${i.product_id||i.id}/?r=${i.id}` },
    shareText(i){ const p=this.formatCurrency(i.resale_price||0),t=i.name||'Produit PettyCash'; return `*${t}*\nSeulement *${p}* sur PettyCash !\n\n${this.shareLink(i)}` },
    async buildShareFile(i){
      if(!i.share_image) return null
      try{ const r=await fetch(i.share_image); if(!r.ok) return null; const b=await r.blob(); return new File([b],'product.jpg',{type:b.type||'image/jpeg'}) }
      catch{ return null }
    },
    async shareWhatsApp(i){
      const text=this.shareText(i), link=this.shareLink(i)
      try{
        const file=await this.buildShareFile(i)
        if(file&&navigator.share&&navigator.canShare&&navigator.canShare({files:[file]})){
          await navigator.share({files:[file],text,url:link})
          return
        }
      }catch(e){if(e?.name==='AbortError')return}
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`,'_blank','noopener,noreferrer')
    },
    shareFacebook(i){ window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.facebookShareLink(i))}`,'_blank','noopener,noreferrer') },
    async writeClipboard(text){
      try{await navigator.clipboard.writeText(text)}
      catch{const t=document.createElement('textarea');t.value=text;document.body.appendChild(t);t.select();document.execCommand('copy');document.body.removeChild(t)}
    },
    async shareNative(i){
      const text=this.shareText(i), link=this.shareLink(i)
      if(navigator.share){
        try{
          const payload={text,url:link}
          const file=await this.buildShareFile(i)
          if(file&&navigator.canShare?.( {files:[file]} )) payload.files=[file]
          await navigator.share(payload)
          return
        }catch(e){if(e?.name==='AbortError')return}
      }
      await this.writeClipboard(link)
      this.copiedId=i.id; this.copiedSnackbar=true
      clearTimeout(this.copiedTimer); this.copiedTimer=setTimeout(()=>{this.copiedId=null},1800)
    },
    formatLarge(v){
      if(!v||parseFloat(v)===0) return '0'
      const n=parseFloat(v)
      if(n>=1000000) return (n/1000000).toFixed(1).replace('.0','')+'M'
      return new Intl.NumberFormat('fr-FR').format(Math.round(n))
    },
    formatCurrency(v){ if(!v) return '0 FCFA'; return new Intl.NumberFormat('fr-FR').format(parseFloat(v))+' FCFA' },
    formatDate(d){ if(!d) return '—'; return new Date(d).toLocaleDateString('fr-FR',{day:'numeric',month:'short',year:'numeric'}) },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

/* ── BASE ── */
.rd {
  font-family: 'Inter', sans-serif;
  -webkit-font-smoothing: antialiased;
  background: #F0F1F7;
  min-height: 100vh;
}

/* ── HERO ── */
.rd-hero {
  background: #1C1C28;
  padding: 28px 24px 36px;
  color: #fff;
}

.rd-hero__top {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 28px;
}

.rd-hero__eyebrow {
  font-size: 11px; font-weight: 600;
  letter-spacing: 1.2px; text-transform: uppercase;
  color: rgba(255,255,255,.4);
}

.rd-hero__bell { opacity: .6; }

.rd-hero__balance-label {
  font-size: 12px; color: rgba(255,255,255,.45);
  font-weight: 500; margin-bottom: 8px;
}

.rd-hero__balance {
  font-size: clamp(38px, 9vw, 60px);
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1;
  margin-bottom: 28px;
}

.rd-hero__currency {
  font-size: .38em;
  font-weight: 500;
  opacity: .5;
  letter-spacing: 0;
  margin-left: 6px;
}

/* Quick actions */
.rd-hero__actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.rd-action-btn {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  background: rgba(255,255,255,.15);
  border: 1px solid rgba(255,255,255,.32);
  border-radius: 16px;
  padding: 14px 8px 12px;
  color: #fff;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(15,23,42,.20), inset 0 1px 0 rgba(255,255,255,.18);
  transition: background .15s, transform .12s, box-shadow .15s;
  font-family: 'Inter', sans-serif;
}
.rd-action-btn__icon {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,.22);
  display: flex; align-items: center; justify-content: center;
  box-shadow: inset 0 -2px 6px rgba(15,23,42,.12);
}
.rd-action-btn:hover  { background: rgba(255,255,255,.24); transform: translateY(-2px); box-shadow: 0 10px 22px rgba(15,23,42,.28), inset 0 1px 0 rgba(255,255,255,.2); }
.rd-action-btn:active { transform: scale(.96); }
.rd-action-btn--off   { opacity: .35; cursor: not-allowed; }

/* ── STATS ── */
.rd-stats {
  display: flex; align-items: center;
  background: #fff;
  margin: 0 16px;
  margin-top: -20px;
  border-radius: 16px;
  padding: 18px 20px;
  position: relative; z-index: 10;
  box-shadow: 0 2px 16px rgba(0,0,0,.08);
}

.rd-stat { flex: 1; text-align: center; }
.rd-stat__val { font-size: 16px; font-weight: 800; color: #1C1C28; line-height: 1; }
.rd-stat__val--green { color: #06C270; }
.rd-stat__lbl { font-size: 10px; color: #9BA3AF; font-weight: 500; margin-top: 5px; text-transform: uppercase; letter-spacing: .4px; }
.rd-stat-sep  { width: 1px; height: 32px; background: #EAECF0; flex-shrink: 0; }

/* ── BODY ── */
.rd-body {
  padding: 20px 16px 40px;
}

@media (min-width: 960px) {
  .rd-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: 24px 24px 48px;
  }
  /* hide mobile tabs on desktop */
  .rd-tabs { display: none !important; }
  .rd-card--hidden { display: block !important; }
}

/* ── MOBILE TABS ── */
.rd-tabs {
  display: flex;
  background: #E4E6EF;
  border-radius: 10px;
  padding: 3px;
  margin-bottom: 14px;
}

.rd-tab {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px;
  padding: 9px;
  border-radius: 8px;
  font-size: 13px; font-weight: 600; color: #6B7280;
  border: none; background: none; cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: all .18s;
}
.rd-tab--on { background: #fff; color: #1C1C28; box-shadow: 0 1px 6px rgba(0,0,0,.09); }

.rd-tab__n {
  font-size: 10px; font-weight: 700;
  background: #EAECF4; color: #5A55F4;
  padding: 1px 6px; border-radius: 100px;
}
.rd-tab--on .rd-tab__n { background: #EAECF4; }

/* ── CARD ── */
.rd-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #EAECF0;
  margin-bottom: 14px;
}

@media (max-width: 959px) {
  .rd-card--hidden { display: none; }
}

.rd-card__head {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #F3F4F6;
}

.rd-card__title { font-size: 14px; font-weight: 700; color: #1C1C28; }

/* ── SEARCH ── */
.rd-search {
  display: flex; align-items: center; gap: 6px;
  background: #F5F6FA; border-radius: 100px;
  padding: 6px 12px;
}
.rd-search__inp {
  border: none; background: none; outline: none;
  font-size: 12px; font-family: 'Inter', sans-serif;
  color: #1C1C28; width: 130px;
}
.rd-search__inp::placeholder { color: #C4C9D4; }

/* ── FILTER TABS ── */
.rd-ftabs { display: flex; gap: 4px; }
.rd-ftab {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 100px;
  font-size: 11px; font-weight: 600;
  border: 1px solid #EAECF0; background: #fff; color: #6B7280;
  cursor: pointer; font-family: 'Inter', sans-serif;
  transition: all .14s;
}
.rd-ftab:hover:not(.rd-ftab--on) { border-color: #5A55F4; color: #5A55F4; }
.rd-ftab--on { background: #5A55F4; color: #fff; border-color: transparent; }
.rd-ftab__n {
  font-size: 9px; font-weight: 700; padding: 1px 4px; border-radius: 100px;
  background: rgba(255,255,255,.25);
}
.rd-ftab:not(.rd-ftab--on) .rd-ftab__n { background: #EAECF4; color: #5A55F4; }

/* ── PRODUCT LIST ── */
.rd-product-list { padding: 6px 12px 12px; display: flex; flex-direction: column; gap: 2px; }

.rd-product {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 6px;
  border-radius: 10px;
  transition: background .12s;
}
.rd-product:hover { background: #FAFAFA; }

.rd-product__img {
  width: 52px; height: 52px; border-radius: 10px;
  border: 1px solid #F0F1F7; background: #F5F6FA;
  overflow: hidden; flex-shrink: 0;
}
.rd-product__img-ph {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center; background: #EAECF4;
}

.rd-product__info { flex: 1; min-width: 0; }
.rd-product__name  { font-size: 13px; font-weight: 600; color: #1C1C28; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rd-product__meta  { font-size: 11px; color: #9BA3AF; margin-top: 2px; }
.rd-product__price { font-size: 12px; font-weight: 700; color: #06C270; margin-top: 5px; }

.rd-product__share { display: flex; flex-direction: column; gap: 4px; flex-shrink: 0; }

.rd-sh {
  width: 26px; height: 26px; border-radius: 7px;
  border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: transform .12s;
}
.rd-sh:hover  { transform: scale(1.1); }
.rd-sh:active { transform: scale(.93); }
.rd-sh--wa { background: rgba(37,211,102,.1); }
.rd-sh--fb { background: rgba(24,119,242,.1); }
.rd-sh--sh { background: #6366f1; }
.rd-sh--ln { background: #F5F6FA; }
.rd-sh--ok { background: rgba(6,194,112,.1); }

/* ── FEED ── */
.rd-feed { padding: 6px 16px 12px; display: flex; flex-direction: column; }

.rd-txn {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #F5F6FA;
}
.rd-txn:last-child { border-bottom: none; }

.rd-txn__ico {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}

.rd-txn__body { flex: 1; min-width: 0; }
.rd-txn__label { font-size: 13px; font-weight: 600; color: #1C1C28; }
.rd-txn__sub   { font-size: 11px; color: #9BA3AF; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.rd-txn__right { text-align: right; flex-shrink: 0; }
.rd-txn__amount { font-size: 13px; font-weight: 800; }
.amt--pos { color: #06C270; }
.amt--neg { color: #EF4444; }
.rd-txn__date { font-size: 10px; color: #C4C9D4; margin-top: 3px; }

/* ── EMPTY / NOTICE ── */
.rd-empty {
  padding: 48px 24px;
  display: flex; flex-direction: column; align-items: center; text-align: center;
}
.rd-empty__title { font-size: 14px; font-weight: 700; color: #374151; margin-bottom: 6px; }
.rd-empty__body  { font-size: 12px; color: #9BA3AF; max-width: 240px; line-height: 1.6; }

.rd-cta {
  margin-top: 16px;
  background: #1C1C28; color: #fff;
  border: none; border-radius: 100px;
  padding: 10px 20px;
  font-size: 12px; font-weight: 700;
  cursor: pointer; font-family: 'Inter', sans-serif;
  transition: opacity .15s;
}
.rd-cta:hover { opacity: .85; }

.rd-notice--err {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 18px;
  font-size: 12px; color: #B91C1C;
  background: rgba(239,68,68,.05);
  border-left: 3px solid #EF4444;
}
.rd-retry {
  background: none; border: none;
  font-size: 11px; font-weight: 700; color: #EF4444;
  cursor: pointer; margin-left: auto;
  font-family: 'Inter', sans-serif;
}

/* ── SKELETON ── */
@keyframes shim { 0%{background-position:-300px 0} 100%{background-position:300px 0} }
.rd-skel {
  background: linear-gradient(90deg, #F0F1F7 25%, #E8EAEF 50%, #F0F1F7 75%);
  background-size: 300px 100%;
  animation: shim 1.2s infinite;
  border-radius: 6px;
}
.rd-skel--sq     { width:52px; height:52px; border-radius:10px; flex-shrink:0; }
.rd-skel--circle { width:36px; height:36px; border-radius:50%;  flex-shrink:0; }
.rd-skel--line   { height:10px; }
.rd-skel--amount { width:72px; height:16px; }
.rd-skels        { padding:10px 16px 14px; }
.rd-skel-row     { display:flex; align-items:center; gap:12px; padding:11px 0; border-bottom:1px solid #F5F6FA; }
.rd-skel-row:last-child { border-bottom:none; }
.mt2 { margin-top: 8px; }

/* ── MODAL ── */
.rd-modal {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
}
.rd-modal--full {
  border-radius: 0;
  min-height: 100vh;
}

.rd-steps {
  display: flex; gap: 5px;
  padding: 16px 20px 0;
}
.rd-step { flex: 1; height: 3px; border-radius: 100px; background: #F0F1F7; transition: background .25s; }
.rd-step--on { background: #5A55F4; }

.rd-modal__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px 0;
}
.rd-modal__title { font-size: 15px; font-weight: 700; color: #1C1C28; }
.rd-modal__nav {
  width: 30px; height: 30px; border-radius: 8px;
  background: #F5F6FA; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .13s;
}
.rd-modal__nav:hover { background: #EAECF0; }

.rd-modal__avail {
  margin: 14px 20px 0;
  background: #F5F6FA;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px; color: #6B7280;
  display: flex; justify-content: space-between; align-items: center;
}
.rd-modal__avail strong { font-size: 15px; font-weight: 800; color: #5A55F4; }

.rd-modal__body { padding: 18px 20px 24px; }

/* dest options */
.rd-dest {
  display: flex; align-items: center; gap: 12px;
  padding: 14px;
  border: 1.5px solid #EAECF0; border-radius: 12px;
  cursor: pointer; margin-bottom: 8px;
  transition: border-color .14s, background .14s;
}
.rd-dest:hover { border-color: #5A55F4; }
.rd-dest--on   { border-color: #5A55F4; background: #F5F4FF; }

.rd-dest__text { flex: 1; }
.rd-dest__name { font-size: 13px; font-weight: 600; color: #1C1C28; }
.rd-dest__hint { font-size: 11px; color: #9BA3AF; margin-top: 2px; }

.rd-dest__dot {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid #EAECF0;
  transition: all .14s;
}
.rd-dest__dot--on { border-color: #5A55F4; background: #5A55F4; box-shadow: 0 0 0 3px rgba(90,85,244,.15); }

/* amount display */
.rd-amt-display {
  text-align: center;
  font-size: 34px; font-weight: 900; letter-spacing: -1.5px;
  color: #EAECF0;
  padding: 16px 0 4px;
  transition: color .2s;
}
.rd-amt-display--set { color: #5A55F4; }

/* pct chips */
.rd-pcts { display: flex; gap: 6px; margin-top: 12px; }
.rd-pct {
  flex: 1; padding: 8px 0; border-radius: 8px;
  border: 1.5px solid #EAECF0; background: #fff;
  font-size: 12px; font-weight: 600; color: #6B7280;
  cursor: pointer; font-family: 'Inter', sans-serif;
  transition: all .13s;
}
.rd-pct:hover  { border-color: #5A55F4; color: #5A55F4; }
.rd-pct--on    { background: #5A55F4; color: #fff; border-color: transparent; }

/* summary */
.rd-summary { background: #F5F6FA; border-radius: 12px; padding: 4px 16px; margin-bottom: 4px; }
.rd-summary__row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #EAECF0;
  font-size: 13px; color: #6B7280;
}
.rd-summary__row:last-child { border-bottom: none; }
.rd-summary__row strong { color: #1C1C28; font-size: 14px; }
.rd-summary__big { font-size: 20px; font-weight: 900; color: #5A55F4; }

.rd-err-msg {
  margin-top: 10px; padding: 10px 14px;
  background: rgba(239,68,68,.06);
  border-left: 3px solid #EF4444;
  border-radius: 8px;
  font-size: 12px; color: #B91C1C;
}

.rd-modal__cta {
  display: block; width: 100%; margin-top: 18px;
  padding: 14px;
  background: #1C1C28; color: #fff;
  border: none; border-radius: 12px;
  font-size: 14px; font-weight: 700;
  cursor: pointer; font-family: 'Inter', sans-serif;
  transition: opacity .14s, transform .12s;
}
.rd-modal__cta:hover  { opacity: .88; }
.rd-modal__cta:active { transform: scale(.98); }

/* Vuetify btn override for confirm step */
.rd-modal__cta.v-btn { border-radius: 12px !important; height: 50px !important; font-weight: 700 !important; letter-spacing: 0 !important; }

/* ── UTILS ── */
.mt16 { margin-top: 16px; }

/* ── LARGE SCREEN hero ── */
@media (min-width: 960px) {
  .rd-hero {
    border-radius: 0;
    padding: 40px 48px 52px;
  }
  .rd-stats {
    margin: 0 24px;
    margin-top: -24px;
  }
}
</style>
